// Phase 10B — Upcoming appointments side panel restyled in the AutoMind 2050
// design language. Same logic + props; only JSX + classNames changed.

import React from 'react';
import {
  Calendar as CalendarIcon,
  Clock,
  Phone,
  User,
  Check,
  Trash2,
  MessageCircle,
  Bell,
  Loader2,
} from 'lucide-react';
import { DataCard } from '../../components/ui/data-card';
import { StatusPill } from '../../components/ui/status-pill';

const statusTone = (status) => {
  switch (status) {
    case 'confirmed':
      return 'success';
    case 'scheduled':
      return 'info';
    case 'completed':
      return 'neutral';
    case 'cancelled':
      return 'danger';
    case 'no_show':
      return 'warning';
    default:
      return 'neutral';
  }
};

const getTypeIcon = (type) => {
  const icons = {
    callback: Phone,
    sales_meeting: User,
    service: Clock,
    demo: CalendarIcon,
    consultation: User,
  };
  return icons[type] || CalendarIcon;
};

const directionDot = (callDirection) =>
  callDirection === 'inbound' ? 'bg-emerald-500' : 'bg-sky-500';

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const formatTime = (timeString) => {
  if (!timeString) return 'TBD';
  const [hours, minutes] = timeString.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  const displayMinutes = String(minutes).padStart(2, '0');
  return `${displayHours}:${displayMinutes} ${period}`;
};

// Redact phone numbers to ***<last4>.
const redactPhone = (phone) => {
  if (!phone) return '—';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length < 4) return '***';
  return `***${digits.slice(-4)}`;
};

const UpcomingAppointmentsList = ({
  appointments,
  sendingSMS,
  onUpdateStatus,
  onDelete,
  onSendConfirmation,
  onSendReminder,
}) => {
  const upcoming = appointments
    .filter((apt) => {
      const d = new Date(apt.appointment_date);
      return (
        d >= new Date() &&
        apt.status !== 'cancelled' &&
        apt.status !== 'completed'
      );
    })
    .sort((a, b) => new Date(a.appointment_date) - new Date(b.appointment_date))
    .slice(0, 5);

  return (
    <DataCard
      title="Upcoming"
      description={`Next ${upcoming.length} scheduled`}
      empty={upcoming.length === 0}
      emptyTitle="Nothing on deck"
      emptyDescription="Upcoming appointments will appear here as they are booked."
      emptyIcon={CalendarIcon}
    >
      <div className="space-y-3">
        {upcoming.map((apt) => {
          const TypeIcon = getTypeIcon(apt.type);
          return (
            <div
              key={apt.id}
              className="row-2050 p-3 rounded-lg border border-ink-200/60 dark:border-ink-800/60 hover:bg-ink-50/60 dark:hover:bg-ink-900/40 transition-colors"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${directionDot(apt.call_direction)} flex-shrink-0`}
                  />
                  <TypeIcon className="w-4 h-4 text-ink-500 flex-shrink-0" />
                  <h3 className="font-medium text-sm truncate">
                    {apt.customer_name}
                  </h3>
                </div>
                <StatusPill tone={statusTone(apt.status)} dot label={apt.status} />
              </div>

              <div className="space-y-1 text-xs text-ink-500">
                <div className="flex items-center gap-2 nums">
                  <CalendarIcon className="w-3 h-3" />
                  {formatDate(apt.appointment_date)}
                  <span>·</span>
                  {formatTime(apt.appointment_time)}
                </div>
                <div className="flex items-center gap-2 nums">
                  <Phone className="w-3 h-3" />
                  {redactPhone(apt.customer_phone)}
                </div>
              </div>

              {apt.notes && (
                <p className="mt-2 text-xs text-ink-500 italic truncate">
                  “{apt.notes}”
                </p>
              )}

              <div className="flex items-center gap-1 mt-3 flex-wrap">
                <button
                  type="button"
                  className="btn-2050 btn-ghost"
                  onClick={() => onSendConfirmation(apt.id)}
                  disabled={sendingSMS[apt.id] === 'confirmation'}
                  data-testid={`send-confirmation-${apt.id}`}
                >
                  {sendingSMS[apt.id] === 'confirmation' ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <MessageCircle className="w-3 h-3" />
                  )}
                  Confirm
                </button>
                <button
                  type="button"
                  className="btn-2050 btn-ghost"
                  onClick={() => onSendReminder(apt.id)}
                  disabled={
                    sendingSMS[apt.id] === 'reminder' || apt.reminder_sent
                  }
                  data-testid={`send-reminder-${apt.id}`}
                >
                  {sendingSMS[apt.id] === 'reminder' ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Bell className="w-3 h-3" />
                  )}
                  {apt.reminder_sent ? 'Reminded' : 'Remind'}
                </button>
                <button
                  type="button"
                  className="btn-2050 btn-ghost"
                  onClick={() => onUpdateStatus(apt.id, 'completed')}
                >
                  <Check className="w-3 h-3" />
                  Complete
                </button>
                <button
                  type="button"
                  className="btn-2050 btn-ghost"
                  onClick={() => onDelete(apt.id)}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </DataCard>
  );
};

export default UpcomingAppointmentsList;
